/**
 * Tesouro Direto mark-to-market simulation.
 *
 * Combines the pricing engine (pricing.ts), business calendar and tax rules
 * to answer: "if I buy this title on date D (past or present) and the market
 * rate is X at horizon H, what is the title worth, what would I net after
 * custody and taxes, and what annual return does that represent?".
 *
 * Real-rate instruments (IPCA+/IGP-M+) price future flows in real terms and
 * convert to nominal with a user-supplied inflation assumption; Selic and
 * prefixed titles discount nominal flows at the scenario rate directly.
 */

import { addMonths, calendarDaysBetween, formatIsoDate, parseIsoDate } from './business-calendar';
import {
  irrAnnualEffective,
  priceFromCashFlows,
  scheduleWithReference,
  yearFraction,
  type DatedAmount,
  type FixedIncomeFlow,
  type FixedIncomeFlowType,
  type FixedIncomeSchedule,
} from './pricing';
import { COUPON_IR_RATE, gainTax } from './tax';

const PRINCIPAL_AMOUNT = 1000;
const MAX_SERIES_POINTS = 750;

export type SimulationQuote = {
  date: string;
  buyRate: number | null;
  sellRate: number | null;
  buyPu: number | null;
  sellPu: number | null;
};

export type SimulationInstrument = {
  idCem: string;
  name: string;
  titleType: string;
  indexer: string;
  cashFlowType: string;
  riskFactor: string;
  maturityDate: string;
  conversionDate: string | null;
};

export type SimulationFlow = {
  paymentDate: string;
  flowType: string;
  amount: number | null;
  indexer: string | null;
};

export type SimulationInput = {
  instrument: SimulationInstrument;
  flows: SimulationFlow[];
  quotes: SimulationQuote[];
  purchaseDate: string;
  amount: number;
  horizonDate: string;
  scenarioRatePct: number;
  inflationAssumptionPct: number;
  custodyFeeRate: number;
  /** Published sell rate (% a.a.) used to anchor the schedule to market. */
  calibrationRatePct?: number | null;
  /** Published sell PU at the reference date used to anchor the schedule. */
  calibrationPu?: number | null;
  overridePurchasePu?: number | null;
};

export type SimulationSaleOutcome = {
  date: string;
  pu: number;
  grossValue: number;
  holdingDays: number;
  irRate: number;
  iofRate: number;
  irDue: number;
  iofDue: number;
  custodyEstimate: number;
  netValue: number;
  netMultiple: number;
  netAnnualReturn: number | null;
  couponsNet: number;
};

export type SimulationSeriesPoint = { date: string; pu: number; kind: 'history' | 'scenario' };

export type SimulationResult = {
  purchase: {
    date: string;
    quoteDateUsed: string;
    rate: number | null;
    pu: number;
    isApproximateDate: boolean;
    quantity: number;
    invested: number;
  };
  today: { date: string; pu: number; value: number; multiple: number } | null;
  horizon: SimulationSaleOutcome;
  maturity: SimulationSaleOutcome;
  series: SimulationSeriesPoint[];
  isRealIndexed: boolean;
  inflationFactorToHorizon: number;
  warnings: string[];
};

/** Real-rate (hybrid) titles price flows in real terms and need an inflation assumption. */
export const isRealRateIndexed = (indexer: string): boolean => {
  const value = indexer.toUpperCase();
  return value.includes('IPCA') || value.includes('IGPM');
};

const sortedQuotes = (quotes: SimulationQuote[]): SimulationQuote[] =>
  [...quotes].sort((a, b) => a.date.localeCompare(b.date));

const lastQuoteOnOrBefore = (quotes: SimulationQuote[], date: Date): SimulationQuote | null => {
  let found: SimulationQuote | null = null;
  for (const quote of quotes) {
    if (parseIsoDate(quote.date).getTime() <= date.getTime()) found = quote;
    else break;
  }
  return found;
};

const toScheduleFlows = (flows: SimulationFlow[]): FixedIncomeFlow[] =>
  flows
    .filter((flow) => flow.amount != null && flow.amount > 0)
    .map((flow) => ({
      paymentDate: parseIsoDate(flow.paymentDate),
      flowType: flow.flowType as FixedIncomeFlowType,
      amount: flow.amount ?? 0,
      indexer: flow.indexer,
    }));

const inferCadenceMonths = (flows: FixedIncomeFlow[]): number | null => {
  const dates = Array.from(new Set(flows.map((flow) => flow.paymentDate.getTime()))).sort((a, b) => a - b);
  if (dates.length < 2) return null;
  const first = new Date(dates[0]);
  const second = new Date(dates[1]);
  const months = (second.getUTCFullYear() - first.getUTCFullYear()) * 12 + (second.getUTCMonth() - first.getUTCMonth());
  return months > 0 ? months : null;
};

/**
 * Rebuild flows paid between the purchase date and the schedule reference
 * (today) by stepping the earliest future COUPON/AMORTIZATION flows back at
 * their inferred cadence. The stored schedule only holds future flows, but
 * Treasury coupons and annuity installments are constant periodic payments,
 * so the backfilled amounts match the paid ones.
 */
const extendFlowsBackwards = (flows: FixedIncomeFlow[], purchaseDate: Date, referenceDate: Date): FixedIncomeFlow[] => {
  const cadence = inferCadenceMonths(flows);
  if (cadence == null) return flows;
  const groups = new Map<string, FixedIncomeFlow>();
  for (const flow of flows) {
    if (flow.flowType === 'PRINCIPAL') continue;
    const key = `${flow.flowType}:${flow.amount}`;
    const current = groups.get(key);
    if (!current || flow.paymentDate < current.paymentDate) groups.set(key, flow);
  }
  const extended = [...flows];
  groups.forEach((seed) => {
    // The stream is only being paid if its first installment minus one
    // cadence step has already passed; a future stream (e.g. a RendA+
    // annuity years before conversion) has no historical payments to
    // reconstruct.
    if (addMonths(seed.paymentDate, -cadence).getTime() > referenceDate.getTime()) return;
    let paymentDate = addMonths(seed.paymentDate, -cadence);
    while (paymentDate.getTime() > purchaseDate.getTime()) {
      if (paymentDate.getTime() <= referenceDate.getTime()) {
        extended.push({ ...seed, paymentDate });
      }
      paymentDate = addMonths(paymentDate, -cadence);
    }
  });
  return extended.sort((a, b) => a.paymentDate.getTime() - b.paymentDate.getTime());
};

const buildSchedule = (instrument: SimulationInstrument, flows: SimulationFlow[], warnings: string[]) => {
  const mapped = toScheduleFlows(flows);
  if (mapped.length > 0) {
    return { flows: mapped, isFallbackBullet: false };
  }
  const terminalDate = parseIsoDate(instrument.conversionDate ?? instrument.maturityDate);
  if (instrument.cashFlowType !== 'BULLET') {
    warnings.push(
      'Agenda de fluxos não disponível para este título; o cenário assume um pagamento único de R$ 1.000 no vencimento.',
    );
  }
  return {
    flows: [
      {
        paymentDate: terminalDate,
        flowType: 'PRINCIPAL' as FixedIncomeFlowType,
        amount: PRINCIPAL_AMOUNT,
        indexer: null,
      },
    ],
    isFallbackBullet: true,
  };
};

const strided = <T>(items: T[], maxPoints: number): T[] => {
  if (items.length <= maxPoints) return items;
  const stride = Math.ceil(items.length / maxPoints);
  return items.filter((_, index) => index % stride === 0);
};

export const simulateTreasuryMarkToMarket = (input: SimulationInput): SimulationResult => {
  const warnings: string[] = [];
  const { instrument } = input;
  const quotes = sortedQuotes(input.quotes);
  if (quotes.length === 0) throw new Error('no quotes available for simulation');
  if (!(input.amount > 0)) throw new Error('invested amount must be positive');

  const isReal = isRealRateIndexed(instrument.indexer);
  const scenarioRate = input.scenarioRatePct / 100;
  const inflationAnnual = input.inflationAssumptionPct / 100;

  const requestedPurchase = parseIsoDate(input.purchaseDate);
  const firstQuote = quotes[0];
  let purchaseDate = requestedPurchase;
  if (purchaseDate.getTime() < parseIsoDate(firstQuote.date).getTime()) {
    purchaseDate = parseIsoDate(firstQuote.date);
    warnings.push(
      `A série deste título começa em ${firstQuote.date}; a data de compra foi ajustada para a primeira cotação disponível.`,
    );
  }

  const purchaseQuote = lastQuoteOnOrBefore(quotes, purchaseDate) ?? firstQuote;
  const isApproximateDate = purchaseQuote.date !== input.purchaseDate;
  if (isApproximateDate) {
    warnings.push(`Não houve cotação em ${input.purchaseDate}; foi usada a cotação de ${purchaseQuote.date}.`);
  }
  const purchasePu =
    input.overridePurchasePu && input.overridePurchasePu > 0
      ? input.overridePurchasePu
      : (purchaseQuote.buyPu ?? purchaseQuote.sellPu);
  if (purchaseQuote.buyPu == null) {
    warnings.push('PU de compra indisponível nesta data; foi usado o PU de venda.');
  }
  if (purchasePu == null || !(purchasePu > 0)) throw new Error('purchase price must be positive');
  const purchaseRate = purchaseQuote.buyRate ?? purchaseQuote.sellRate;
  const quantity = input.amount / purchasePu;

  const maturityDate = parseIsoDate(instrument.maturityDate);
  const conversionDate = instrument.conversionDate ? parseIsoDate(instrument.conversionDate) : null;
  const todayQuote = quotes[quotes.length - 1];
  const today = parseIsoDate(todayQuote.date);

  const scheduleFlows = buildSchedule(instrument, input.flows, warnings);

  // The contract schedule is normalized (principal of R$ 1.000 in real
  // terms), while the published PU embeds index accumulation since the
  // title's base date. Scale the flows so the model reproduces today's
  // published PU at today's published rate; the projection then evolves
  // from the actual market price instead of an arbitrary nominal anchor.
  const calibrationPu = input.calibrationPu ?? null;
  const calibrationRate =
    input.calibrationRatePct != null && input.calibrationRatePct > 0 ? input.calibrationRatePct / 100 : null;
  let calibrationApplied = false;
  if (calibrationPu != null && calibrationPu > 0 && calibrationRate != null) {
    const modelPu = priceFromCashFlows(
      { referenceDate: today, flows: scheduleFlows.flows, dayCountConvention: 'BUSINESS_252' },
      calibrationRate,
    );
    if (modelPu > 0 && Number.isFinite(modelPu)) {
      const factor = calibrationPu / modelPu;
      if (Math.abs(factor - 1) > 0.001) {
        scheduleFlows.flows = scheduleFlows.flows.map((flow) => ({ ...flow, amount: flow.amount * factor }));
        warnings.push(
          'A agenda contratual normalizada foi calibrada ao PU publicado; a projeção parte do preço de mercado atual.',
        );
      }
      calibrationApplied = true;
    }
  }

  const schedule: FixedIncomeSchedule = {
    referenceDate: today,
    flows: extendFlowsBackwards(scheduleFlows.flows, purchaseDate, today),
    dayCountConvention: 'BUSINESS_252',
  };

  const inflationFactorTo = (date: Date): number =>
    isReal ? Math.pow(1 + inflationAnnual, yearFraction(purchaseDate, date)) : 1;

  const principalFlow =
    schedule.flows.find((flow) => flow.flowType === 'PRINCIPAL') ?? schedule.flows[schedule.flows.length - 1];
  const redemptionPu = (principalFlow?.amount ?? PRINCIPAL_AMOUNT) * inflationFactorTo(maturityDate);

  const puAt = (date: Date): number => {
    if (date.getTime() >= maturityDate.getTime()) return redemptionPu;
    const priced = priceFromCashFlows(scheduleWithReference(schedule, date), scenarioRate);
    return priced * inflationFactorTo(date);
  };

  // Sales up to the last quote are factual: use the published PU instead of
  // the scenario price ("what if I had sold back then").
  const knownPuOnOrBefore = (date: Date): number | null => {
    if (date.getTime() > today.getTime()) return null;
    const quote = lastQuoteOnOrBefore(quotes, date);
    return quote?.sellPu ?? null;
  };

  // Effective horizon: capped by maturity and by the conversion date of
  // annuity titles (RendA+/Educa+), where monthly amortizations start.
  let horizonDate = parseIsoDate(input.horizonDate);
  const horizonCeiling = conversionDate && conversionDate < maturityDate ? conversionDate : maturityDate;
  if (horizonDate.getTime() > horizonCeiling.getTime()) {
    horizonDate = horizonCeiling;
    warnings.push(
      conversionDate
        ? `O horizonte foi limitado à data de conversão (${formatIsoDate(conversionDate)}), quando começam os pagamentos mensais.`
        : `O horizonte foi limitado ao vencimento (${formatIsoDate(maturityDate)}).`,
    );
  }
  if (horizonDate.getTime() <= purchaseDate.getTime()) {
    horizonDate =
      conversionDate && conversionDate > purchaseDate ? conversionDate : new Date(purchaseDate.getTime() + 86_400_000);
    warnings.push('O horizonte deve ser posterior à data de compra; ele foi ajustado.');
  }

  const couponsNetUntil = (sale: Date): number => {
    let total = 0;
    for (const flow of schedule.flows) {
      if (flow.paymentDate.getTime() <= purchaseDate.getTime()) continue;
      if (flow.paymentDate.getTime() > sale.getTime()) break;
      if (flow.flowType !== 'COUPON' && flow.flowType !== 'AMORTIZATION') continue;
      const nominalAmount = flow.amount * inflationFactorTo(flow.paymentDate);
      const netRate = flow.flowType === 'COUPON' ? 1 - COUPON_IR_RATE : 1;
      total += nominalAmount * netRate * quantity;
    }
    return total;
  };

  const custodyEstimateUntil = (sale: Date): number => {
    // Trapezoid integration of custodyFeeRate × position value over time.
    const points: { date: Date; pu: number }[] = [{ date: purchaseDate, pu: purchasePu }];
    const windowEnd = sale < today ? sale : today;
    for (const quote of quotes) {
      const date = parseIsoDate(quote.date);
      if (date.getTime() <= purchaseDate.getTime()) continue;
      if (date.getTime() > windowEnd.getTime()) break;
      if (quote.sellPu != null) points.push({ date, pu: quote.sellPu });
    }
    if (sale > today) {
      let step = addMonths(today, 1);
      while (step < sale) {
        points.push({ date: step, pu: puAt(step) });
        step = addMonths(step, 1);
      }
      points.push({ date: sale, pu: puAt(sale) });
    } else {
      points.push({ date: sale, pu: knownPuOnOrBefore(sale) ?? puAt(sale) });
    }
    let integral = 0;
    for (let index = 1; index < points.length; index += 1) {
      const dt = calendarDaysBetween(points[index - 1].date, points[index].date) / 365;
      integral += ((points[index - 1].pu + points[index].pu) / 2) * dt;
    }
    return input.custodyFeeRate * quantity * integral;
  };

  const saleOutcome = (sale: Date): SimulationSaleOutcome => {
    const pu = knownPuOnOrBefore(sale) ?? puAt(sale);
    const grossValue = pu * quantity;
    const tax = gainTax(purchaseDate, sale, grossValue - input.amount);
    const custodyEstimate = custodyEstimateUntil(sale);
    const couponsNet = couponsNetUntil(sale);
    const netValue = grossValue - tax.totalTax - custodyEstimate;
    const cashFlows: DatedAmount[] = [
      { date: purchaseDate, amount: -input.amount },
      ...schedule.flows
        .filter(
          (flow) =>
            (flow.flowType === 'COUPON' || flow.flowType === 'AMORTIZATION') &&
            flow.paymentDate.getTime() > purchaseDate.getTime() &&
            flow.paymentDate.getTime() <= sale.getTime(),
        )
        .map((flow) => ({
          date: flow.paymentDate,
          amount:
            flow.amount *
            (flow.flowType === 'COUPON' ? 1 - COUPON_IR_RATE : 1) *
            inflationFactorTo(flow.paymentDate) *
            quantity,
        })),
      { date: sale, amount: netValue },
    ];
    return {
      date: formatIsoDate(sale),
      pu,
      grossValue,
      holdingDays: tax.holdingDays,
      irRate: tax.irRate,
      iofRate: tax.iofRate,
      irDue: tax.irAmount,
      iofDue: tax.iofAmount,
      custodyEstimate,
      netValue,
      netMultiple: netValue / input.amount,
      netAnnualReturn: irrAnnualEffective(cashFlows),
      couponsNet,
    };
  };

  const horizon = saleOutcome(horizonDate);
  const maturity = saleOutcome(maturityDate);

  const todayPu = todayQuote.sellPu;
  const todayBlock =
    todayPu != null
      ? { date: todayQuote.date, pu: todayPu, value: todayPu * quantity, multiple: (todayPu * quantity) / input.amount }
      : null;

  // Without a market anchor, flag when the normalized schedule diverges from
  // the published PU (notably RendA+/Educa+ before conversion).
  if (!calibrationApplied && todayPu != null && todayPu > 0) {
    const divergence = Math.abs(puAt(today) - todayPu) / todayPu;
    if (divergence > 0.2) {
      warnings.push(
        'A projeção usa a agenda contratual normalizada e diverge do PU publicado deste título (típico em RendA+ e Educa+ antes da conversão, cujas parcelas incorporam rendimento acumulado). Interprete os valores absolutos com cautela.',
      );
    }
  }

  const historyPoints: SimulationSeriesPoint[] = strided(
    quotes
      .filter((quote) => quote.date >= formatIsoDate(purchaseDate) && quote.sellPu != null)
      .map((quote) => ({ date: quote.date, pu: quote.sellPu as number, kind: 'history' as const })),
    MAX_SERIES_POINTS,
  );

  const scenarioPoints: SimulationSeriesPoint[] = [];
  let step = today;
  while (step < maturityDate) {
    scenarioPoints.push({ date: formatIsoDate(step), pu: puAt(step), kind: 'scenario' });
    step = addMonths(step, 1);
  }
  scenarioPoints.push({ date: formatIsoDate(maturityDate), pu: redemptionPu, kind: 'scenario' });

  return {
    purchase: {
      date: formatIsoDate(purchaseDate),
      quoteDateUsed: purchaseQuote.date,
      rate: purchaseRate,
      pu: purchasePu,
      isApproximateDate,
      quantity,
      invested: input.amount,
    },
    today: todayBlock,
    horizon,
    maturity,
    series: [...historyPoints, ...scenarioPoints],
    isRealIndexed: isReal,
    inflationFactorToHorizon: inflationFactorTo(horizonDate),
    warnings,
  };
};
